import { useState } from "react";
import { Circle, Phone, Clock, Voicemail, CalendarCheck, XCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logActivity } from "@/lib/staffActivity";
import { useAuth } from "@/hooks/useAuth";
import { useTeamRole } from "@/hooks/useTeamRole";
import type { DbLead, LeadStatus } from "@/lib/types";

const STATUSES: { value: LeadStatus | "not_interested"; label: string; Icon: any; color: string; bg: string; border: string }[] = [
  { value: "new", label: "New", Icon: Circle, color: "text-muted-foreground", bg: "bg-muted/50", border: "border-muted" },
  { value: "contacted", label: "Contacted", Icon: Phone, color: "text-primary", bg: "bg-primary/10", border: "border-primary" },
  { value: "follow_up", label: "Follow Up", Icon: Clock, color: "text-warning", bg: "bg-warning/10", border: "border-warning" },
  { value: "voicemail", label: "Voicemail Left", Icon: Voicemail, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-300" },
  { value: "booked", label: "Booked", Icon: CalendarCheck, color: "text-success", bg: "bg-success/10", border: "border-success" },
  { value: "not_interested", label: "Not Interested", Icon: XCircle, color: "text-hot", bg: "bg-hot/10", border: "border-hot" },
];

interface Props {
  lead: DbLead | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function StatusUpdateSheet({ lead, onClose, onSaved }: Props) {
  const { user } = useAuth();
  const { agencyId } = useTeamRole();
  const [selected, setSelected] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  if (!lead) return null;

  const canonicalStatus = (val: string): LeadStatus => {
    if (val === "not_interested") return "lost";
    return val as LeadStatus;
  };

  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    try {
      const status = canonicalStatus(selected);
      const { error } = await supabase
        .from("leads")
        .update({ status, acuity_notes: note ? `${lead.acuity_notes ?? ""}\n[Staff note] ${note}`.trim() : lead.acuity_notes })
        .eq("id", lead.id);
      if (error) throw error;

      if (user && agencyId) {
        await logActivity(user.id, agencyId, "status_updated", lead.id);
      }

      if (selected === "booked") {
        setShowConfetti(true);
        setTimeout(() => {
          setShowConfetti(false);
          toast.success("🎉 Booked! Great work.");
          onSaved();
          onClose();
        }, 1200);
      } else {
        toast.success("Status updated");
        onSaved();
        onClose();
      }
    } catch (err: any) {
      toast.error(err.message ?? "Failed to update status");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <div className="fixed left-0 right-0 bottom-0 z-50 bg-card rounded-t-2xl shadow-2xl flex flex-col max-h-[85vh] sm:h-[70vh] animate-in slide-in-from-bottom duration-200">
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="h-1.5 w-10 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Header */}
        <div className="px-6 pb-3 text-center relative">
          <h2 className="text-lg font-bold text-navy">Update Status</h2>
          <p className="text-base text-muted-foreground mt-0.5">{lead.name}</p>
          <button onClick={onClose} className="absolute right-4 top-0 p-2 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Status grid */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-2 gap-3">
            {STATUSES.map(s => {
              const isSelected = selected === s.value;
              return (
                <button
                  key={s.value}
                  onClick={() => setSelected(s.value)}
                  className={`rounded-xl border-2 p-4 transition-all flex flex-col items-center gap-2 ${
                    isSelected ? `${s.bg} ${s.border}` : "border-border bg-card hover:border-muted-foreground/30"
                  }`}
                >
                  <s.Icon className={`h-6 w-6 ${s.color}`} fill={s.value === "new" && isSelected ? "currentColor" : "none"} />
                  <span className={`text-sm font-bold ${isSelected ? s.color : "text-navy"}`}>{s.label}</span>
                </button>
              );
            })}
          </div>

          {selected && (
            <div className="mt-5">
              <label className="text-sm font-medium text-navy mb-1.5 block">Note (optional)</label>
              <Textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Add a note about this interaction..."
                rows={3}
                className="text-base"
              />
            </div>
          )}
        </div>

        {/* Save bar */}
        <div className="px-6 py-4 border-t bg-card sticky bottom-0">
          <Button
            onClick={handleSave}
            disabled={!selected || saving}
            className="w-full min-h-[48px] bg-gold text-gold-foreground hover:bg-gold/90 font-bold"
          >
            {saving ? "Saving..." : "Save Update"}
          </Button>
        </div>

        {/* Confetti */}
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                className="absolute top-0 h-2 w-2 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  backgroundColor: ["#D4A843", "#22c55e", "#3b82f6", "#a855f7"][i % 4],
                  animation: `confetti-fall ${1 + Math.random()}s ease-in forwards`,
                  animationDelay: `${Math.random() * 0.3}s`,
                }}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(0) rotate(0); opacity: 1; }
          100% { transform: translateY(600px) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </>
  );
}
